import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "./cn";
import { CircleNotch as Loader2 } from "@phosphor-icons/react";

/**
 * Button variant and size configuration.
 * Maps to Brand Guidelines v3.0 button patterns.
 */
const variants = {
  /** Primary gradient button */
  default:
    "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/25",
  /** White CTA on dark backgrounds */
  white:
    "bg-white hover:bg-white/90 text-[#2D274B] shadow-sm hover:shadow-xl hover:-translate-y-0.5",
  /** Outline / secondary action */
  outline:
    "border border-border bg-transparent hover:bg-accent hover:text-accent-foreground",
  /** Ghost / tertiary */
  ghost: "hover:bg-accent hover:text-accent-foreground",
  /** Destructive / danger */
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
  /** Link style */
  link: "text-primary underline-offset-4 hover:underline",
} as const;

/**
 * Horizontal padding is deliberately tighter than the usual shadcn defaults.
 * Buttons here mostly appear in dense clusters — toolbars, action pills, card
 * footers — where the generous padding cost more room than it bought, and the
 * fixed heights already give each button its presence.
 *
 * Heights are untouched on purpose: they set the vertical rhythm that inputs,
 * badges and rows across both apps are aligned to, so changing them would
 * reach far beyond the buttons themselves.
 *
 * Shared with the marketing site (@charisol-spark/website) — `xl` keeps more
 * room than the rest because it is the landing-page CTA size, where the button
 * is the focal point rather than one control among many.
 */
const sizes = {
  sm: "h-9 px-3 text-sm",
  default: "h-10 px-4 py-2 text-sm",
  lg: "h-12 px-6 text-base",
  xl: "h-14 px-7 text-base font-semibold",
  icon: "h-10 w-10",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  /** Show loading spinner and disable interaction */
  loading?: boolean;
  /** Pill-shaped (full border radius) */
  pill?: boolean;
}

/**
 * Button component following the Spark Design Guideline.
 *
 * @example
 * <Button variant="white" size="xl" pill>Join Waitlist</Button>
 * <Button variant="outline" pill>Contact Sales</Button>
 * <Button loading>Processing...</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      loading = false,
      pill = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 font-medium transition-all duration-300",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          pill ? "rounded-full" : "rounded-md",
          variants[variant],
          sizes[size],
          className,
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
